/*!
 * CRO Utility Engine v1.0.0 - Production Complete
 * Integrated Command Mode + Direct Mode + Storage + Multi-Actions
 */

; (function (win, doc) {
	'use strict';

	if (win.CRO_UTILS) {
		console.warn('[CRO] Already initialized');
		return;
	}

	const CRO_UTILS = {
		version: '1.0.0',
		executed: new Set(),
		utils: {},
		storage: {},
		_aborts: [],
		_observers: [],
		_timers: [],
		_executionLog: [],
		_settings: {
			debug: false,
			maxObservers: 50,
			maxTimers: 100,
			storagePrefix: 'cro_',
			logExecutionHistory: true,
			allowDirectJS: true
		}
	};

	const log = {
		debug: (...args) => CRO_UTILS._settings.debug && console.log('[CRO]', ...args),
		warn: (...args) => CRO_UTILS._settings.debug && console.warn('[CRO]', ...args),
		error: (...args) => console.error('[CRO ERROR]', ...args),
		action: (cmd, idx, type, status, details = {}) => {
			const entry = { timestamp: Date.now(), command: cmd, actionIndex: idx, actionType: type, status, details };
			if (CRO_UTILS._settings.logExecutionHistory) CRO_UTILS._executionLog.push(entry);
			if (CRO_UTILS._settings.debug) {
				const emoji = status === 'success' ? '✓' : status === 'failed' ? '✗' : '○';
				console.log(`[CRO] ${emoji} ${cmd} → #${idx + 1} (${type}): ${status}`, details);
			}
		}
	};

	function createAbort() {
		const ctrl = { aborted: false, callbacks: [] };
		ctrl.abort = () => {
			ctrl.aborted = true;
			ctrl.callbacks.forEach(cb => { try { cb(); } catch (e) { } });
			ctrl.callbacks = [];
		};
		ctrl.onAbort = (cb) => ctrl.aborted ? cb() : ctrl.callbacks.push(cb);
		CRO_UTILS._aborts.push(ctrl);
		return ctrl;
	}

	CRO_UTILS.abortAll = () => {
		CRO_UTILS._aborts.forEach(a => a.abort());
		CRO_UTILS._aborts = [];
	};

	function trackObserver(obs) {
		if (CRO_UTILS._observers.length >= CRO_UTILS._settings.maxObservers) {
			const old = CRO_UTILS._observers.shift();
			try { old.disconnect(); } catch (e) { }
		}
		CRO_UTILS._observers.push(obs);
		return obs;
	}

	function trackTimer(t) {
		if (CRO_UTILS._timers.length >= CRO_UTILS._settings.maxTimers) {
			const old = CRO_UTILS._timers.shift();
			try { clearInterval(old); clearTimeout(old); } catch (e) { }
		}
		CRO_UTILS._timers.push(t);
		return t;
	}

	function cleanupObservers() {
		CRO_UTILS._observers.forEach(o => { try { o.disconnect(); } catch (e) { } });
		CRO_UTILS._observers = [];
	}

	function cleanupTimers() {
		CRO_UTILS._timers.forEach(t => { try { clearInterval(t); clearTimeout(t); } catch (e) { } });
		CRO_UTILS._timers = [];
	}

	function getKey(k) {
		return k && typeof k === 'string' ? CRO_UTILS._settings.storagePrefix + k : null;
	}

	function sanitizeValue(v) {
		if (v === null || v === undefined) return '';
		if (typeof v === 'object') {
			try { return JSON.stringify(v); } catch (e) { return ''; }
		}
		return String(v);
	}

	function parseValue(v) {
		if (!v) return v;
		try { return JSON.parse(v); } catch (e) { return v; }
	}

	function isStorageAvailable(type) {
		try {
			const s = win[type], test = '__cro__';
			s.setItem(test, test);
			s.removeItem(test);
			return true;
		} catch (e) { return false; }
	}

	// STORAGE
	CRO_UTILS.storage.local = {
		get: (k, parse = true) => {
			const key = getKey(k);
			if (!key || !isStorageAvailable('localStorage')) return null;
			try {
				const v = win.localStorage.getItem(key);
				return v === null ? null : (parse ? parseValue(v) : v);
			} catch (e) { return null; }
		},
		set: (k, v) => {
			const key = getKey(k);
			if (!key || !isStorageAvailable('localStorage')) return false;
			try { win.localStorage.setItem(key, sanitizeValue(v)); return true; } catch (e) { return false; }
		},
		remove: (k) => {
			const key = getKey(k);
			if (!key || !isStorageAvailable('localStorage')) return false;
			try { win.localStorage.removeItem(key); return true; } catch (e) { return false; }
		},
		exists: function (k) { return this.get(k, false) !== null; },
		clear: () => {
			if (!isStorageAvailable('localStorage')) return 0;
			const prefix = CRO_UTILS._settings.storagePrefix;
			let count = 0;
			try {
				const keys = [];
				for (let i = 0; i < win.localStorage.length; i++) {
					const k = win.localStorage.key(i);
					if (k && k.startsWith(prefix)) keys.push(k);
				}
				keys.forEach(k => { win.localStorage.removeItem(k); count++; });
			} catch (e) { }
			return count;
		}
	};

	CRO_UTILS.storage.session = {
		get: (k, parse = true) => {
			const key = getKey(k);
			if (!key || !isStorageAvailable('sessionStorage')) return null;
			try {
				const v = win.sessionStorage.getItem(key);
				return v === null ? null : (parse ? parseValue(v) : v);
			} catch (e) { return null; }
		},
		set: (k, v) => {
			const key = getKey(k);
			if (!key || !isStorageAvailable('sessionStorage')) return false;
			try { win.sessionStorage.setItem(key, sanitizeValue(v)); return true; } catch (e) { return false; }
		},
		remove: (k) => {
			const key = getKey(k);
			if (!key || !isStorageAvailable('sessionStorage')) return false;
			try { win.sessionStorage.removeItem(key); return true; } catch (e) { return false; }
		},
		exists: function (k) { return this.get(k, false) !== null; },
		clear: () => {
			if (!isStorageAvailable('sessionStorage')) return 0;
			const prefix = CRO_UTILS._settings.storagePrefix;
			let count = 0;
			try {
				const keys = [];
				for (let i = 0; i < win.sessionStorage.length; i++) {
					const k = win.sessionStorage.key(i);
					if (k && k.startsWith(prefix)) keys.push(k);
				}
				keys.forEach(k => { win.sessionStorage.removeItem(k); count++; });
			} catch (e) { }
			return count;
		}
	};

	CRO_UTILS.storage.cookie = {
		get: (k, parse = true) => {
			const key = getKey(k);
			if (!key) return null;
			try {
				const m = doc.cookie.match(new RegExp('(^| )' + key + '=([^;]+)'));
				if (!m) return null;
				const v = decodeURIComponent(m[2]);
				return parse ? parseValue(v) : v;
			} catch (e) { return null; }
		},
		set: (k, v, opts = {}) => {
			const key = getKey(k);
			if (!key) return false;
			try {
				const val = sanitizeValue(v);
				const days = opts.days !== undefined ? opts.days : 7;
				const exp = new Date(Date.now() + days * 864e5).toUTCString();
				let c = key + '=' + encodeURIComponent(val) + ';expires=' + exp + ';path=' + (opts.path || '/');
				if (opts.domain) c += ';domain=' + opts.domain;
				if (opts.secure) c += ';secure';
				if (opts.sameSite) c += ';samesite=' + opts.sameSite;
				doc.cookie = c;
				return true;
			} catch (e) { return false; }
		},
		remove: function (k, opts = {}) { return this.set(k, '', { ...opts, days: -1 }); },
		exists: function (k) { return this.get(k, false) !== null; },
		clear: () => {
			const prefix = CRO_UTILS._settings.storagePrefix;
			let count = 0;
			try {
				doc.cookie.split(';').forEach(c => {
					const n = c.split('=')[0].trim();
					if (n.startsWith(prefix)) {
						doc.cookie = n + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
						count++;
					}
				});
			} catch (e) { }
			return count;
		}
	};

	// UTILS
	CRO_UTILS.utils.waitFor = (sels, opts = {}) => {
		return new Promise((res, rej) => {
			try {
				const timeout = opts.timeout || 10000;
				const parent = opts.parent || doc;
				const abort = opts.abort || { aborted: false };
				const start = Date.now();
				if (!Array.isArray(sels)) sels = [sels];
				abort.onAbort?.(() => rej(new Error('aborted')));
				(function poll() {
					if (abort.aborted) return rej(new Error('aborted'));
					for (let s of sels) {
						try {
							const el = parent.querySelector(s);
							if (el) return res(el);
						} catch (e) { }
					}
					if (Date.now() - start > timeout) return res(null);
					win.requestAnimationFrame(poll);
				})();
			} catch (e) { res(null); }
		});
	};

	CRO_UTILS.utils.waitForElement = (sel, cb, opts = {}) => {
		const iv = opts.interval || 100, to = opts.timeout || 10000;
		const abort = opts.abort || { aborted: false }, start = Date.now();
		const t = setInterval(() => {
			if (abort.aborted) return clearInterval(t);
			const el = doc.querySelector(sel);
			if (el) { clearInterval(t); cb(el); }
			if (Date.now() - start > to) clearInterval(t);
		}, iv);
		trackTimer(t);
		abort.onAbort?.(() => clearInterval(t));
	};

	CRO_UTILS.utils.eleReady = (sel, cb, opts = {}) => {
		const to = opts.timeout || 10000, abort = opts.abort || { aborted: false };
		let trig = false;
		const obs = new MutationObserver(() => {
			if (abort.aborted || trig) return obs.disconnect();
			const el = doc.querySelector(sel);
			if (el) { trig = true; obs.disconnect(); cb(el); }
		});
		obs.observe(doc.documentElement, { childList: true, subtree: true });
		trackObserver(obs);
		const t = setTimeout(() => { if (!trig) obs.disconnect(); }, to);
		trackTimer(t);
		abort.onAbort?.(() => { clearTimeout(t); obs.disconnect(); });
	};

	CRO_UTILS.utils.waitUntil = (pred, succ, opts = {}) => {
		const iv = opts.interval || 200, to = opts.timeout || 10000;
		const abort = opts.abort || { aborted: false }, start = Date.now();
		const t = setInterval(() => {
			if (abort.aborted) return clearInterval(t);
			if (pred()) { clearInterval(t); succ(); }
			if (Date.now() - start > to) clearInterval(t);
		}, iv);
		trackTimer(t);
		abort.onAbort?.(() => clearInterval(t));
	};

	CRO_UTILS.utils.addClass = (el, cls) => {
		if (!el || !cls) return false;
		try { cls.split(' ').forEach(c => c && el.classList.add(c)); return true; } catch (e) { return false; }
	};
	CRO_UTILS.utils.removeClass = (el, cls) => {
		if (!el || !cls) return false;
		try { cls.split(' ').forEach(c => c && el.classList.remove(c)); return true; } catch (e) { return false; }
	};
	CRO_UTILS.utils.toggleClass = (el, cls) => {
		if (!el || !cls) return false;
		try { el.classList.toggle(cls); return true; } catch (e) { return false; }
	};
	CRO_UTILS.utils.hide = (el) => {
		if (!el) return false;
		try { el.setAttribute('data-cro-d', el.style.display || ''); el.style.display = 'none'; return true; } catch (e) { return false; }
	};
	CRO_UTILS.utils.show = (el) => {
		if (!el) return false;
		try { el.style.display = el.getAttribute('data-cro-d') || ''; el.removeAttribute('data-cro-d'); return true; } catch (e) { return false; }
	};
	CRO_UTILS.utils.setAttr = (el, n, v) => {
		if (!el || !n) return false;
		try { el.setAttribute(n, v || ''); return true; } catch (e) { return false; }
	};
	CRO_UTILS.utils.removeAttr = (el, n) => {
		if (!el || !n) return false;
		try { el.removeAttribute(n); return true; } catch (e) { return false; }
	};
	CRO_UTILS.utils.setText = (el, t) => {
		if (!el || t === undefined) return false;
		try { el.textContent = t; return true; } catch (e) { return false; }
	};
	CRO_UTILS.utils.setHTML = (el, h) => {
		if (!el || h === undefined) return false;
		try { el.innerHTML = h; return true; } catch (e) { return false; }
	};
	CRO_UTILS.utils.getAll = (sel, p) => {
		try { return Array.from((p || doc).querySelectorAll(sel)); } catch (e) { return []; }
	};
	CRO_UTILS.utils.exists = (sel, p) => {
		try { return !!(p || doc).querySelector(sel); } catch (e) { return false; }
	};
	CRO_UTILS.utils.device = (bp) => {
		bp = bp || { mobile: 768, tablet: 1024 };
		const w = win.innerWidth;
		return w <= bp.mobile ? 'mobile' : w <= bp.tablet ? 'tablet' : 'desktop';
	};

	// ACTIONS
	const Actions = {
		addClass: (el, p) => p?.className ? CRO_UTILS.utils.addClass(el, p.className) : false,
		removeClass: (el, p) => p?.className ? CRO_UTILS.utils.removeClass(el, p.className) : false,
		toggleClass: (el, p) => p?.className ? CRO_UTILS.utils.toggleClass(el, p.className) : false,
		hide: (el) => CRO_UTILS.utils.hide(el),
		show: (el) => CRO_UTILS.utils.show(el),
		setAttr: (el, p) => p?.name ? CRO_UTILS.utils.setAttr(el, p.name, p.value) : false,
		removeAttr: (el, p) => p?.name ? CRO_UTILS.utils.removeAttr(el, p.name) : false,
		setText: (el, p) => p?.text !== undefined ? CRO_UTILS.utils.setText(el, p.text) : false,
		setHTML: (el, p) => p?.html ? CRO_UTILS.utils.setHTML(el, p.html) : false,
		create: (el, p) => {
			if (!el || !p?.html) return false;
			try { el.insertAdjacentHTML(p.position || 'beforeend', p.html); return true; } catch (e) { return false; }
		},
		remove: (el) => {
			if (!el) return false;
			try { el.remove(); return true; } catch (e) { return false; }
		},
		setStorage: (el, p) => {
			if (!p?.key || p.value === undefined) return false;
			const t = p.type || 'session';
			try {
				return t === 'local' ? CRO_UTILS.storage.local.set(p.key, p.value) :
					t === 'cookie' ? CRO_UTILS.storage.cookie.set(p.key, p.value, p.options || {}) :
						CRO_UTILS.storage.session.set(p.key, p.value);
			} catch (e) { return false; }
		},
		getStorage: (el, p) => {
			if (!p?.key) return false;
			const t = p.type || 'session';
			try {
				let v = t === 'local' ? CRO_UTILS.storage.local.get(p.key) :
					t === 'cookie' ? CRO_UTILS.storage.cookie.get(p.key) :
						CRO_UTILS.storage.session.get(p.key);
				if (p.callback && typeof p.callback === 'function') p.callback(v, el);
				return v !== null;
			} catch (e) { return false; }
		},
		removeStorage: (el, p) => {
			if (!p?.key) return false;
			const t = p.type || 'session';
			try {
				return t === 'local' ? CRO_UTILS.storage.local.remove(p.key) :
					t === 'cookie' ? CRO_UTILS.storage.cookie.remove(p.key, p.options || {}) :
						CRO_UTILS.storage.session.remove(p.key);
			} catch (e) { return false; }
		},
		directJS: (el, p) => {
			if (!CRO_UTILS._settings.allowDirectJS) return false;
			if (!p || typeof p.fn !== 'function') return false;
			try {
				p.fn(el, CRO_UTILS.utils, CRO_UTILS.storage, CRO_UTILS);
				return true;
			} catch (e) {
				log.error('directJS failed:', e);
				return false;
			}
		}
	};

	// CONDITIONS
	function evalCond(w) {
		if (!w) return true;
		try {
			if (w.urlContains && win.location.href.indexOf(w.urlContains) === -1) return false;
			if (w.urlMatches && !new RegExp(w.urlMatches).test(win.location.href)) return false;
			if (w.pathContains && win.location.pathname.indexOf(w.pathContains) === -1) return false;
			if (w.existsSelector && !CRO_UTILS.utils.exists(w.existsSelector)) return false;
			if (w.notExistsSelector && CRO_UTILS.utils.exists(w.notExistsSelector)) return false;
			if (w.storageExists) {
				const k = w.storageExists.key, t = w.storageExists.type || 'session';
				if (t === 'local' && !CRO_UTILS.storage.local.exists(k)) return false;
				if (t === 'cookie' && !CRO_UTILS.storage.cookie.exists(k)) return false;
				if (t === 'session' && !CRO_UTILS.storage.session.exists(k)) return false;
			}
			if (w.storageEquals) {
				const k = w.storageEquals.key, v = w.storageEquals.value, t = w.storageEquals.type || 'session';
				let sv;
				if (t === 'local') sv = CRO_UTILS.storage.local.get(k);
				else if (t === 'cookie') sv = CRO_UTILS.storage.cookie.get(k);
				else sv = CRO_UTILS.storage.session.get(k);
				if (typeof v === 'object' && typeof sv === 'object') {
					if (JSON.stringify(sv) !== JSON.stringify(v)) return false;
				} else if (sv !== v) return false;
			}
			if (w.runIf && typeof w.runIf === 'function' && !w.runIf()) return false;
			if (w.all && !w.all.every(c => evalCond(c))) return false;
			if (w.any && !w.any.some(c => evalCond(c))) return false;
			return true;
		} catch (e) { return false; }
	}

	// COMMAND VALIDATOR
	function validateCmd(c) {
		if (!c || !c.name) return false;
		const hasSingle = c.do && typeof c.do === 'string';
		const hasMulti = c.actions && Array.isArray(c.actions);
		if (!hasSingle && !hasMulti) return false;
		if (hasSingle && hasMulti) return false;
		if (hasSingle && !Actions[c.do]) return false;
		if (hasMulti) {
			if (c.actions.length === 0) return false;
			for (let a of c.actions) {
				if (!a.do || !Actions[a.do]) return false;
			}
		}
		const storageActs = ['setStorage', 'getStorage', 'removeStorage'];
		const needsSel = hasSingle
			? storageActs.indexOf(c.do) === -1
			: c.actions.some(a => storageActs.indexOf(a.do) === -1);
		if (needsSel && (!c.on || !c.on.selectors || !Array.isArray(c.on.selectors) || c.on.selectors.length === 0)) {
			return false;
		}
		return true;
	}

	// EXECUTE SINGLE ACTION
	function execAct(el, aDef, cmdName, idx) {
		const type = aDef.do, p = aDef.params || {};
		try {
			const start = Date.now();
			const succ = Actions[type](el, p);
			const dur = Date.now() - start;
			log.action(cmdName, idx, type, succ ? 'success' : 'failed', { dur: dur + 'ms' });
			return { success: succ, actionType: type, duration: dur };
		} catch (e) {
			log.action(cmdName, idx, type, 'failed', { error: e.message });
			return { success: false, actionType: type, error: e };
		}
	}

	// EXECUTE MULTIPLE ACTIONS
	function execMulti(el, acts, cmdName) {
		const res = [];
		let succ = 0, abort = false;
		for (let i = 0; i < acts.length; i++) {
			const a = acts[i], req = a.required !== false;
			if (abort) {
				log.action(cmdName, i, a.do, 'skipped', { reason: 'Previous required action failed' });
				res.push({ success: false, actionType: a.do, skipped: true });
				continue;
			}
			const r = execAct(el, a, cmdName, i);
			res.push(r);
			if (r.success) succ++;
			else if (req) abort = true;
		}
		return { totalActions: acts.length, successCount: succ, failedCount: acts.length - succ, results: res, aborted: abort };
	}

	// EXECUTE COMMAND
	function execCmd(cmd, settings) {
		return new Promise((resolve) => {
			try {
				const start = Date.now();
				if (!validateCmd(cmd)) return resolve({ success: false, reason: 'invalid_command' });
				if (cmd.when && !evalCond(cmd.when)) return resolve({ success: false, reason: 'conditions_not_met' });
				if (cmd.runOnce && CRO_UTILS.executed.has(cmd.name)) return resolve({ success: false, reason: 'already_executed' });

				const abort = createAbort();
				const isSingle = !!cmd.do;
				const isMulti = !!cmd.actions;
				const storageActs = ['setStorage', 'getStorage', 'removeStorage'];
				const isStorOnly = isSingle ? storageActs.indexOf(cmd.do) !== -1 : cmd.actions.every(a => storageActs.indexOf(a.do) !== -1);

				if (isStorOnly) {
					try {
						let r;
						if (isSingle) r = execAct(null, { do: cmd.do, params: cmd.params }, cmd.name, 0);
						else {
							const mr = execMulti(null, cmd.actions, cmd.name);
							r = { success: mr.successCount > 0 };
						}
						if (r.success) {
							if (cmd.runOnce) CRO_UTILS.executed.add(cmd.name);
							return resolve({ success: true, duration: Date.now() - start, type: 'storage' });
						}
						return resolve({ success: false, reason: 'storage_action_failed' });
					} catch (e) {
						return resolve({ success: false, reason: 'execution_error', error: e });
					}
				}

				const run = () => {
					try {
						if (abort.aborted) return resolve({ success: false, reason: 'aborted' });
						const sel = cmd.on.selectors.join(',');
						const els = doc.querySelectorAll(sel);
						if (els.length === 0) return resolve({ success: false, reason: 'no_elements' });

						let totSucc = 0;
						const elRes = [];
						els.forEach((el, ei) => {
							try {
								if (isSingle) {
									const r = execAct(el, { do: cmd.do, params: cmd.params }, cmd.name, 0);
									if (r.success) totSucc++;
									elRes.push({ element: ei, result: r });
								} else {
									const mr = execMulti(el, cmd.actions, cmd.name);
									if (mr.successCount > 0) totSucc++;
									elRes.push({ element: ei, result: mr });
								}
							} catch (e) {
								elRes.push({ element: ei, error: e.message });
							}
						});

						const dur = Date.now() - start;
						if (totSucc > 0) {
							if (cmd.runOnce) CRO_UTILS.executed.add(cmd.name);
							resolve({ success: true, count: totSucc, total: els.length, duration: dur, results: elRes });
						} else {
							resolve({ success: false, reason: 'all_actions_failed', results: elRes });
						}
					} catch (e) {
						resolve({ success: false, reason: 'execution_error', error: e });
					}
				};

				const wait = cmd.wait || { strategy: 'poll' };
				const wOpts = { timeout: settings.timeout || wait.timeout || 10000, abort };

				if (wait.strategy === 'observer') {
					CRO_UTILS.utils.eleReady(cmd.on.selectors[0], run, wOpts);
				} else if (wait.strategy === 'until') {
					if (typeof wait.predicate !== 'function') return resolve({ success: false, reason: 'invalid_predicate' });
					CRO_UTILS.utils.waitUntil(wait.predicate, run, wOpts);
				} else {
					CRO_UTILS.utils.waitFor(cmd.on.selectors, { ...wOpts, parent: cmd.on.parent ? doc.querySelector(cmd.on.parent) : doc })
						.then(el => el ? run() : resolve({ success: false, reason: 'element_not_found' }))
						.catch(err => resolve({ success: false, reason: err.message === 'aborted' ? 'aborted' : 'wait_error', error: err }));
				}
			} catch (e) {
				resolve({ success: false, reason: 'setup_error', error: e });
			}
		});
	}

	// PUBLIC API
	CRO_UTILS.init = ({ mode = 'command', config: cfg = {}, commands = [] } = {}) => {
		try {
			Object.assign(CRO_UTILS._settings, cfg);
			log.debug('Init CRO_UTILS v' + CRO_UTILS.version, 'Mode:', mode);

			if (mode === 'command' || mode === 'both') {
				if (!Array.isArray(commands)) {
					log.error('Commands must be array');
					return CRO_UTILS;
				}
				const sorted = commands.sort((a, b) => (a.order || 0) - (b.order || 0));
				const results = [];
				sorted.forEach((cmd, i) => {
					try {
						const p = execCmd(cmd, cfg);
						results.push(p);
						p.then(r => { if (!r.success && CRO_UTILS._settings.debug) log.warn(`Command "${cmd.name}" failed:`, r.reason); });
					} catch (e) {
						log.error('Failed to execute command at index', i, e);
					}
				});
				if (mode === 'both') {
					log.debug('Both mode: Commands executed, direct mode ready');
				}
				return Promise.all(results);
			}

			if (mode === 'direct' || mode === 'both') {
				log.debug('Direct mode active - use CRO_UTILS.utils, CRO_UTILS.storage, CRO_UTILS API');
			}

			if (mode !== 'command' && mode !== 'direct' && mode !== 'both') {
				log.error('Unknown mode:', mode, '- Use "command", "direct", or "both"');
			}

			return CRO_UTILS;
		} catch (e) {
			log.error('Init failed:', e);
			return CRO_UTILS;
		}
	};

	CRO_UTILS.run = (fn) => {
		if (typeof fn !== 'function') {
			log.error('run: Argument must be a function');
			return;
		}
		try {
			fn(CRO_UTILS.utils, CRO_UTILS.storage, CRO_UTILS);
		} catch (e) {
			log.error('run: User function failed:', e);
		}
	};

	CRO_UTILS.execute = (cmd) => execCmd(cmd, CRO_UTILS._settings);

	CRO_UTILS.executeMultiple = (cmds) => Promise.all(cmds.map(c => execCmd(c, CRO_UTILS._settings)));

	CRO_UTILS.cleanup = () => {
		log.debug('Cleaning up CRO_UTILS');
		try {
			CRO_UTILS.abortAll();
			cleanupObservers();
			cleanupTimers();
			CRO_UTILS.executed.clear();
			log.debug('Cleanup complete');
		} catch (e) {
			log.error('Cleanup failed:', e);
		}
	};

	CRO_UTILS.reset = () => {
		log.debug('Resetting CRO_UTILS');
		try {
			CRO_UTILS.cleanup();
			CRO_UTILS._settings = {
				debug: false,
				maxObservers: 50,
				maxTimers: 100,
				storagePrefix: 'cro_',
				logExecutionHistory: true,
				allowDirectJS: true
			};
			log.debug('Reset complete');
		} catch (e) {
			log.error('Reset failed:', e);
		}
	};

	CRO_UTILS.getState = () => ({
		version: CRO_UTILS.version,
		executed: Array.from(CRO_UTILS.executed),
		activeObservers: CRO_UTILS._observers.length,
		activeTimers: CRO_UTILS._timers.length,
		activeAborts: CRO_UTILS._aborts.length,
		settings: { ...CRO_UTILS._settings }
	});

	CRO_UTILS.getExecutionLog = (cmdName) => {
		if (cmdName) {
			return CRO_UTILS._executionLog.filter(e => e.command === cmdName);
		}
		return [...CRO_UTILS._executionLog];
	};

	CRO_UTILS.getExecutionSummary = () => {
		const log = CRO_UTILS._executionLog;
		const cmds = {};
		log.forEach(e => {
			if (!cmds[e.command]) {
				cmds[e.command] = { total: 0, success: 0, failed: 0, skipped: 0 };
			}
			cmds[e.command].total++;
			cmds[e.command][e.status]++;
		});
		return {
			totalActions: log.length,
			commands: cmds,
			successRate: log.length > 0 ? (log.filter(e => e.status === 'success').length / log.length * 100).toFixed(2) + '%' : '0%'
		};
	};

	CRO_UTILS.clearExecutionLog = () => {
		CRO_UTILS._executionLog = [];
		log.debug('Execution log cleared');
	};

	CRO_UTILS.exportExecutionLog = () => {
		return JSON.stringify({
			version: CRO_UTILS.version,
			exportedAt: new Date().toISOString(),
			log: CRO_UTILS._executionLog,
			summary: CRO_UTILS.getExecutionSummary()
		}, null, 2);
	};

	// Legacy global support
	win.waitForElement = (sel, cb) => {
		try { CRO_UTILS.utils.waitForElement(sel, cb); } catch (e) { log.error('Global waitForElement failed:', e); }
	};

	win.eleReady = (sel, cb) => {
		try { CRO_UTILS.utils.eleReady(sel, cb); } catch (e) { log.error('Global eleReady failed:', e); }
	};

	win.waitUntil = (pred, succ) => {
		try { CRO_UTILS.utils.waitUntil(pred, succ); } catch (e) { log.error('Global waitUntil failed:', e); }
	};

	// Export
	win.CRO_UTILS = CRO_UTILS;
	log.debug('CRO_UTILS v' + CRO_UTILS.version + ' loaded successfully');

})(window, document);